import { Origin, Units, displayShortUnits } from './dimensions';
import { prepareDate, uuid } from './helpers';

export interface BoundingBox {
  top: number;
  left: number;
  bottom: number;
  right: number;
  width: number;
  height: number;
}

export interface IUnusableCircle {
  x: number;
  y: number;
  diameter: number;
}

export interface IUnusableRectangle {
  x: number;
  y: number;
  width: number;
  height: number;
}

export enum UnusableTypes {
  round = 'round',
  rectangular = 'rectangular',
}

export type IUnusable = IUnusableCircle | IUnusableRectangle;

export type IPlace = {
  id?: string;
  name?: string;
  location?: string;
  notes?: string;
  units?: Units;
  width?: number;
  height?: number;
  created?: Date;
  updated?: Date;
  origin?: Origin;
  gridspacing?: number;
  unusable?: IUnusable[];
  unusableorigin?: Origin;
};

export class Place {
  id: string;
  type = 'place';
  name: string;
  location: string;
  notes: string;
  units: Units;
  width: number;
  height: number;
  origin: Origin;
  gridspacing: number;
  unusable: IUnusable[];
  unusableorigin: Origin;
  created: string;
  updated: string;

  constructor({
    id = null,
    name = '',
    location = '',
    notes = '',
    units = Units.feet,
    width = null,
    height = null,
    created = null,
    updated = null,
    origin = Origin['Left Top'],
    gridspacing = 10,
    unusable = null,
    unusableorigin = null,
    ...rest
  }: IPlace) {
    const now = new Date();
    this.id = id ? id : uuid();
    this.name = name;
    this.location = location;
    this.notes = notes;
    this.units = units;
    this.width = width;
    this.height = height;
    this.origin = origin;
    this.gridspacing = gridspacing;
    this.created = prepareDate(created, now);
    this.updated = prepareDate(updated, now);
    this.unusable = unusable || [];
    this.unusableorigin = unusableorigin; // leave null until unusable areas are added
    Object.assign(this, rest); // allow old source to edit/persist properties from future versions
  }

  urlId(): string {
    return this.id;
  }

  displayUnits(): string {
    return displayShortUnits(this.units);
  }

  isMetric(): boolean {
    return this.units === Units.meters;
  }

  xOrigin(): string {
    return Place.xOriginStr(this.origin);
  }

  yOrigin(): string {
    return Place.yOriginStr(this.origin);
  }

  widthToHeightRatio(): number {
    return this.width / this.height;
  }

  isSaved(): boolean {
    return Boolean(this.name);
  }

  boundingUnits(): BoundingBox {
    return Place.boundingUnits(this.origin, this.width, this.height);
  }

  static boundingUnits(
    origin: Origin,
    width: number,
    height: number,
  ): BoundingBox {
    let [top, bottom] = this.yMinMax(origin, height);
    const bWidth = Math.abs(bottom - top);
    let [left, right] = this.xMinMax(origin, width);
    const bHeight = Math.abs(right - left);
    if (this.yOriginStr(origin) !== 'Top') {
      [top, bottom] = [bottom, top];
    }
    if (this.xOriginStr(origin) === 'Right') {
      [left, right] = [right, left];
    }
    const x = {
      top: top,
      left: left,
      bottom: bottom,
      right: right,
      width: bWidth,
      height: bHeight,
    };
    return x;
  }

  static xOriginStr(val: number): string {
    const str = Origin[val];
    return str.split(' ')[0];
  }

  static yOriginStr(val: number): string {
    const str = Origin[val];
    return str.split(' ')[1];
  }

  static xMinMax(origin: number, width: number): [number, number] {
    // order is [min, max]
    switch (this.xOriginStr(origin)) {
      case 'Left':
        return [0, width];
      case 'Center':
        const half = width / 2;
        return [-half, half];
      case 'Right':
        return [0, width];
    }
    return [0, 0];
  }

  static yMinMax(origin: number, height: number): [number, number] {
    // order is [min, max]
    switch (this.yOriginStr(origin)) {
      case 'Top':
        return [0, height];
      case 'Center':
        const half = height / 2;
        return [-half, half];
      case 'Bottom':
        return [0, height];
    }
    return [0, 0];
  }
}
